import { Observable } from 'rxjs';

import { BasicInterfaceTypeEnum, CatsStatusCodeEnum } from '@shared-enums/cats.enum';
import { IDayOfWeek, IWeekDayNumbers } from '@shared-interfaces/dates';

import { ICatRecord } from '../interfaces/incoming';
import {
  IEmployeeBillingSummaryRow,
  IEquipmentBillingSummaryRow,
  IProductionQuantityPayrollSummaryRow,
  ITableConfigEntity,
} from '../interfaces/internal-interfaces';
import { IOutgoingCatsArrays } from './catsDB';
import { CatsRecord } from './catsRecord';
import { Employee } from './employee';
import { Equipment } from './equipment';
import { ProductionEquipment } from './productionEquipment';
import { ProductionQuantity } from './ProductionQuantity';
import { ProductionText } from './ProductionText';
import { ITimesheetDate } from './timesheetHandler';

//this class gives universal methods to each type of cats object interface handler.
//types include equipment, employees, production, and production text. Each type also has a cat record type.
export class BasicInterface {
  shouldShow = true; //whenever this is set to false, this entity will not show on a table-like layout.
  shouldNeverShow = false; //when this is true, this entity cannot be shown in table-like layout. Prevents issues when "All" is toggled.
  catsRecords: ICatRecord[] = []; //includes ALL cats records for this entity type
  visibleCatsRecords: ICatRecord[] = []; //excludes records that don't have bearing on payroll or billing, namely records with value 0 on key field. Normally just applies to delete cases after approval
  focusedDay: ITimesheetDate | undefined; //will help the detail payne figure out which day is currently being viewed.
  shouldHighlight = false; //if this is true, the interface is highlighted
  highlightMessage = '';
  filteredTimesheet: string;
  wasDeleteAttempted = false;
  isPayroll = false;
  isBilling = false;
  isExtDoc = false;
  __config: ITableConfigEntity[];

  constructor(public context: IBasicInterfaceContext) {
    this.changeContext(context);
  }

  show() {
    this.shouldShow = true;
  }

  hide() {
    this.shouldShow = false;
  }

  public changeContext(context: IBasicInterfaceContext): void {
    this.context = context;
    this.isPayroll = context === 'payroll';
    this.isBilling = context === 'billing';
    this.isExtDoc = context === 'extDoc';
  }

  checkApprovalStatus(catsRecords: ICatRecord[]): void {
    this.resetHighlight();
    if (this.wasDeleteAttempted) {
      this.setHighlight('Approve deleted records.');
      return;
    }
    let containsUnapproved = false;
    catsRecords.map((x) => {
      const recordStatus = Number(x.Status);
      if (recordStatus < CatsStatusCodeEnum.Approved) containsUnapproved = true;
    });
    if (containsUnapproved) {
      this.setHighlight('This contains unapproved hours.');
    }
  }

  resetHighlight(): void {
    this.shouldHighlight = false;
    this.highlightMessage = '';
  }

  setHighlight(string: string): void {
    this.shouldHighlight = true;
    this.highlightMessage = string;
  }

  getCatRecords(): ICatRecord[] {
    return this.catsRecords; //find all cats records on this entity. probably just used for approval case.
  }
}

export type IBasicInterfaceContext = 'payroll' | 'billing' | 'extDoc' | 'general';
export type IAnyInterfaceType = Employee | Equipment | ProductionQuantity | ProductionText | ProductionEquipment;

export interface IBasicInterfaceContract {
  //This contains class members that ALL classes that extends basicInterface MUST implement!
  display: string;
  detailPayneRecords: CatsRecord[];
  readonly billCodeType: BasicInterfaceTypeEnum;
  addNewRecord: (config: ITableConfigEntity[]) => CatsRecord;
  calculateBillingGrid: (timesheetNo?: string) => void;
  calculatePayrollGrid: () => void;
  filterByTimesheet: (timesheetNo: string) => void;
  getBillingDetailPayneConfig: (record?: ICatRecord) => ITableConfigEntity[];
  getBillingDetailPayneValues: (row: number, column: ITableConfigEntity<IDayOfWeek>) => void;
  getPayrollDetailPayneConfig: (day?: IWeekDayNumbers, record?: ICatRecord) => ITableConfigEntity[];
  getPayrollDetailPayneValues: (row: number, column: ITableConfigEntity<IDayOfWeek>) => void;
  handleCatsRecordsSet: () => void;
  launchPayrollDetailPayneHandler: (row: number, column: ITableConfigEntity<IDayOfWeek>) => void;
  save$: (records: IOutgoingCatsArrays) => Observable<ICatRecord[]>;
  setCatsRecords: (catsRecords: ICatRecord[]) => void;
}

export interface IBasicInterfaceNotProductionTextContract {
  //This contains some shared class members that everything except for ProductionText must implement!
  showBulkAdd: () => void;
  handleMassBillCodeChange$: (current: string, desired: string, timesheetNo: string) => Observable<ICatRecord[]>;
  makeBillingRow: (
    code: string,
    recordPool: ICatRecord[],
  ) => IEmployeeBillingSummaryRow | IEquipmentBillingSummaryRow | IProductionQuantityPayrollSummaryRow;
}
